/**
 * 문제별 정답률 테이블 컴포넌트
 * Ant Design Table을 사용하여 문제집의 문제별 통계 표시
 */

import React from 'react';
import { Card, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ProblemStatItem } from '../../types/stats';

interface ProblemAccuracyTableProps {
  problems: ProblemStatItem[];
  loading?: boolean;
}

/**
 * 답형식 태그 색상 매핑
 */
const getAnswerTypeColor = (answerType: string): string => {
  const colorMap: Record<string, string> = {
    M: 'blue',
    S: 'green',
    B: 'orange',
    N: 'purple',
    E: 'cyan',
    T: 'magenta',
  };
  return colorMap[answerType] || 'default';
};

/**
 * 정답률에 따른 색상 반환
 */
const getAccuracyColor = (accuracy: number): string => {
  if (accuracy >= 80) return '#52c41a'; // 녹색
  if (accuracy >= 60) return '#faad14'; // 주황색
  return '#cf1322'; // 빨간색
};

export const ProblemAccuracyTable: React.FC<ProblemAccuracyTableProps> = ({
  problems,
  loading = false,
}) => {
  const columns: ColumnsType<ProblemStatItem> = [
    {
      title: '페이지',
      dataIndex: 'page',
      key: 'page',
      width: 80,
      sorter: (a, b) => a.page - b.page,
    },
    {
      title: '문제 번호',
      dataIndex: 'problem_number',
      key: 'problem_number',
      width: 100,
    },
    {
      title: '답형식',
      dataIndex: 'answer_type',
      key: 'answer_type',
      width: 100,
      render: (answerType: string) => (
        <Tag color={getAnswerTypeColor(answerType)}>{answerType}</Tag>
      ),
      filters: [
        { text: 'M (객관식)', value: 'M' },
        { text: 'S (보기선택)', value: 'S' },
        { text: 'B (OX)', value: 'B' },
        { text: 'N (숫자)', value: 'N' },
        { text: 'E (수식)', value: 'E' },
        { text: 'T (서술형)', value: 'T' },
      ],
      onFilter: (value, record) => record.answer_type === value,
    },
    {
      title: '제출 수',
      dataIndex: 'total_submissions',
      key: 'total_submissions',
      width: 100,
      sorter: (a, b) => a.total_submissions - b.total_submissions,
    },
    {
      title: '정답 수',
      dataIndex: 'correct_submissions',
      key: 'correct_submissions',
      width: 100,
      sorter: (a, b) => a.correct_submissions - b.correct_submissions,
    },
    {
      title: '정답률 (%)',
      dataIndex: 'accuracy',
      key: 'accuracy',
      width: 120,
      sorter: (a, b) => a.accuracy - b.accuracy,
      render: (accuracy: number) => (
        <span style={{ color: getAccuracyColor(accuracy), fontWeight: 'bold' }}>
          {accuracy.toFixed(1)}%
        </span>
      ),
    },
    {
      title: '평균 시도 횟수',
      dataIndex: 'average_attempts',
      key: 'average_attempts',
      width: 140,
      sorter: (a, b) => a.average_attempts - b.average_attempts,
      render: (attempts: number) => attempts.toFixed(2),
    },
  ];

  return (
    <Card title="문제별 정답률">
      <Table
        columns={columns}
        dataSource={problems}
        loading={loading}
        rowKey={(record) => `${record.page}-${record.problem_number}`}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개 문제`,
        }}
        scroll={{ x: 800 }}
      />
    </Card>
  );
};
